import { useContext, useEffect, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { UsersUI } from "./ui"
import { IUsersUI } from "./types"

import { useUsers } from "@api/users/state"

import { useValue } from "@hooks/useValue"
import { useDelayedInput } from "@hooks/useDelayedInput"

import { CardModeType } from "@smart/common.type"
import { CardUserContainer, ICardUserContainer } from "@smart/cardUser"
import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"
import realtimeService from "@services/realtime.service"
import { reportUnexpectedClientError } from "@services/client-error.service"


/** Контейнер UI - Пользователи системы */
export const UsersContainer = () => {
    const notify = useContext(NotificationContext)

    /** Таймер, для автоматического обновления основного списка */
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    /** Метка о том, что доступ к данному разделу запрещен */
    const isAccessDenied = useValue<boolean>(false)
    /** Пагинация - Изменение номера страницы */
    const paginationPageNumber = useValue<number>(1)

    /** Окно с анкетой заявки */
    const cardUser = useValue<ICardUserContainer>({isOpen: false})
    /** Окно подтверждения */
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})


    // Хранилище страницы - Администрирование
    const { 
        isLoadingPage, usersList, canManageAvatars, canEditUsers, canDeleteUsers,
        usersPageSize, usersTotalCount, 

        clearStateUsers, actionGetUsers, actionDeleteUserCard,
    } = useUsers(useShallow((state) => ({
        isLoadingPage: state.users.isLoading,
        canManageAvatars: state.users.canRead,
        canEditUsers: state.users.canRead && state.users.canEdit,
        canDeleteUsers: state.users.canRead && state.users.canDelete,

        usersList: state.users.items,
        usersPageSize: state.users.pageSize,
        usersTotalCount: state.users.totalCount,

        clearStateUsers: state.clearStateUsers,
        actionGetUsers: state.actionGetUsers,
        actionDeleteUserCard: state.actionDeleteUserCard,
    })))

    /** Загрузка первичных данных */
    const loadPrimaryData = (search: string, isShowLoading: boolean = true) => {
        actionGetUsers(paginationPageNumber.value, search, isShowLoading).then((res) => {
            if (res.status === 200) isAccessDenied.setValue(false)
            if (res.status !== 200 && res.status !== 500) {
                if (res.status === 403) {
                    clearStateUsers()
                    isAccessDenied.setValue(true)
                } else {
                    notify && notifyOpen(res.message, 'error', 2000, notify)
                }
            }
        }).catch((res) => {
            reportUnexpectedClientError(res, 'Users.actionGetUsers')
            notify && notifyOpen(res.message, 'error', 2000, notify)
        })
    }

    /** Запуск загрузки данных через полсекунды */
    const { value: txtSearch, setValue: setTxtSearch } = useDelayedInput<string>("", 1000, (val) => loadPrimaryData(val))
    const searchRef = useRef(txtSearch)

    // ================================================================================

    useEffect(() => {
        return (() => {
            clearStateUsers()

            setTxtSearch('')
            paginationPageNumber.setValue(1)
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        searchRef.current = txtSearch
    }, [txtSearch])

    useEffect(() => {
        const unsubscribeUsers = realtimeService.subscribe('users', () => {
            isAccessDenied.value === false && loadPrimaryData(searchRef.current, false)
        })

        intervalRef.current = setInterval(() => {
            isAccessDenied.value === false && loadPrimaryData(searchRef.current, false)
        }, 60000)

        return () => {
            unsubscribeUsers()

            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadPrimaryData(txtSearch)
    }, [paginationPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps
    
    // ================================================================================

    /** Открытие/закрытие окна подтверждения */
    const handleOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionType: string, actionID: number = 0) => {
        if (isOpen && actionType === "deleteUser" && (!useUsers.getState().users.canRead || !useUsers.getState().users.canDelete)) return
        modalConfirm.setValue({
            isOpen, headerText, contentText, 

            handleButtonCancel() {
                modalConfirm.setValue({isOpen: false})
            }, 
            
            handleButtonOk() {
                switch (actionType) {
                    case "deleteUser": { // Удаление анкеты оператора
                        if (!useUsers.getState().users.canRead || !useUsers.getState().users.canDelete) return
                        actionDeleteUserCard(actionID).then((res) => {
                            if (res.status === 403) {
                                return notify && notifyOpen(res.message, 'error', 2000, notify)
                            }

                            if (res.status === 200) {
                                loadPrimaryData(txtSearch)
                                modalConfirm.setValue({isOpen: false})
                                notify && notifyOpen('Анкета оператора успешно удалена', 'success', 2000, notify)
                            }
                        })
                        break
                    }
                }
            }
        })
    }

    /** Открытие/закрытие анкеты оператора */
    const handleOpenCardUser = (isOpen: boolean, cardMode: CardModeType, cardID?: number) => {
        if (isOpen && (!useUsers.getState().users.canRead || (cardMode === "new" && !useUsers.getState().users.canEdit))) return
        cardUser.setValue({
            isOpen, cardMode, cardID,
            
            handleButtonCancel() {
                cardUser.setValue({isOpen: false})
            }, 
            
            handleButtonOk(isClosed) {
                if (isClosed) cardUser.setValue({isOpen: false})
                loadPrimaryData(txtSearch)
            }
        })
    }

    // ================================================================================

    /** Свойства передаваемые во вкладку пользователи */
    const propsToUI: IUsersUI = {
        isLoadingPage,
        canManageAvatars, canEditUsers, canDeleteUsers,
        usersList,
        usersPageSize,
        usersTotalCount,
        paginationPageNumber,
        
        txtSearch, isAccessDenied,
        
        setTxtSearch, loadPrimaryData, handleOpenCardUser, handleOpenModalConfirm,
    }


    return <>
        <UsersUI {...propsToUI} />

        <CardUserContainer {...cardUser.value} isOpen={cardUser.value.isOpen && canManageAvatars} />
        <ModalConfirm {...modalConfirm.value} isOpen={modalConfirm.value.isOpen && canDeleteUsers} />
    </>
}
